import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Usar window.location.origin para evitar Mixed Content
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    // A landing CREDTech não usa carrinho. Evita criar sessão e consultar a
    // API durante a medição e durante a primeira visita comercial.
    const carrinhoDesabilitado = /^\/(credito|credtech)\/?$/.test(window.location.pathname);
    if (carrinhoDesabilitado) return;

    // Get or create session ID
    let sid = localStorage.getItem('cart_session_id');
    if (!sid) {
      sid = `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('cart_session_id', sid);
    }
    setSessionId(sid);
    loadCart(sid);
  }, []);

  const loadCart = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/pagamentos/carrinho/${sid}`);
      setItems(response.data.itens || []);
    } catch (error) {
      console.error('Erro ao carregar carrinho:', error);
    }
  };

  const addItem = async (item) => {
    if (!sessionId) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/api/pagamentos/carrinho/adicionar?session_id=${sessionId}`,
        item
      );
      setItems(response.data.itens || []);
      setIsOpen(true); // Open cart when item is added
      return true;
    } catch (error) {
      console.error('Erro ao adicionar item:', error);
      return false;
    }
  };

  const removeItem = async (itemId) => {
    if (!sessionId) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/api/pagamentos/carrinho/${sessionId}/item/${itemId}`
      );
      setItems(response.data.itens || []);
    } catch (error) {
      console.error('Erro ao remover item:', error);
    }
  };

  const clearCart = async () => {
    if (!sessionId) return;
    
    try {
      await axios.delete(`${API_URL}/api/pagamentos/carrinho/${sessionId}`);
      setItems([]);
    } catch (error) {
      console.error('Erro ao limpar carrinho:', error);
    }
  };

  const getTotal = () => {
    return items.reduce((sum, item) => sum + (item.valor * item.quantidade), 0);
  };

  const getItemCount = () => {
    return items.reduce((sum, item) => sum + item.quantidade, 0);
  };

  const value = {
    items,
    sessionId,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    clearCart,
    getTotal,
    getItemCount,
    refreshCart: () => loadCart(sessionId)
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
